// src/pages/admin/users/UsersTable.jsx

import React from 'react';
import PropTypes from 'prop-types';
import UserRoleBadge from '../../../shared/components/UserRoleBadge';
import MarkerPill from '../../../shared/components/pill/MarkerPill';
import UserAccountStatusBadge from '../../../shared/components/UserAccountStatusBadge';
import DataTable from '../common/DataTable';
import UserAvatar from './UserAvatar';
import { useCurrentUser } from '../../auth/CurrentUserContext';
import { canAssignRole, canModifyUser } from '../../auth/permissions';

export default function UsersTable({ items, filterExtras, onEditRole, onResendInvite, onDeactivate, onReactivate }) {
  const { getCurrentUserEmail, user: currentUser } = useCurrentUser();
  const currentUserEmail = getCurrentUserEmail();

  const columns = [
    {
      key: 'user',
      label: 'User',
      sortable: true,
      filterValue: row => `${row.given_name ?? ''} ${row.family_name ?? ''} ${row.email ?? ''}`.trim(),
      render: row => (
        <div className="d-flex align-items-center">
          <UserAvatar user={row} size={40} className="me-2" />
          <div>
            <div className="fw-semibold">
              {row.given_name || row.family_name
                ? `${row.given_name ?? ''} ${row.family_name ?? ''}`.trim()
                : '—'}
              {row.email === currentUserEmail && (
                <MarkerPill tone="primary" className="ms-2">You</MarkerPill>
              )}
            </div>
            <div className="small text-muted">{row.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      label: 'Role',
      sortable: true,
      filterValue: row => row.role || '',
      render: row => (
        <UserRoleBadge role={row.role} />
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      filterValue: row => row.status || '',
      render: row => (
        <UserAccountStatusBadge status={row.status} />
      ),
    },
    {
      key: 'last_login',
      label: 'Last Active',
      sortable: true,
      filterValue: row => row.last_sign_in_at || row.created_at,
      render: row => formatLastActive(row.last_sign_in_at, row.created_at),
      className: 'text-nowrap',
    },
  ];

  const actions = [
    {
      key: 'edit',
      label: 'Edit role',
      icon: 'bi-pencil',
      onClick: onEditRole,
      visible: row => canAssignRole(currentUser, row, row.role),
    },
    {
      key: 'resend',
      label: 'Resend invite',
      icon: 'bi-envelope-arrow-up',
      onClick: onResendInvite,
      visible: row => canModifyUser(currentUser, row) && row.status === 'pending',
    },
    {
      key: 'deactivate',
      label: 'Deactivate',
      icon: 'bi-person-dash',
      onClick: onDeactivate,
      visible: row => canModifyUser(currentUser, row) && row.status !== 'deactivated',
    },
    {
      key: 'reactivate',
      label: 'Reactivate',
      icon: 'bi-person-check',
      onClick: onReactivate,
      visible: row => canModifyUser(currentUser, row) && row.status === 'deactivated',
    },
  ];

  return (
    <DataTable
      items={items}
      columns={columns}
      actions={actions}
      filterExtras={filterExtras}
      filterPlaceholder="Search users…"
      emptyMessage="No users found."
    />
  );
}

// --- Helpers ---
function formatLastActive(lastLogin, createdAt) {
  const ts = lastLogin || createdAt;
  if (!ts) return '—';
  const d = new Date(ts);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH} hour${diffH > 1 ? 's' : ''} ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD} day${diffD > 1 ? 's' : ''} ago`;
  return d.toLocaleDateString();
}

UsersTable.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  filterExtras: PropTypes.node,
  onEditRole: PropTypes.func,
  onResendInvite: PropTypes.func,
  onDeactivate: PropTypes.func,
  onReactivate: PropTypes.func,
};

UsersTable.defaultProps = {
  onEditRole: () => {},
  filterExtras: null,
  onResendInvite: () => {},
  onDeactivate: () => {},
  onReactivate: () => {},
};
